import { TFunction } from 'i18next';
import {
  mapFromIntegration,
  mapFromIntegrationConnection,
  mapFromIntegrationResult,
  mapFromIntegrationRun,
  mapFromIntegrationRunStats,
  mapFromIntegrations,
  mapFromIntegrationTypes,
  mapToIntegration,
  mapToIntegrationConnection,
  mapToIntegrations,
} from 'mappings/integrationMapping';
import {
  GitHubInstallCompleteDTO,
  GitHubInstallStartDTO,
  GitHubInstallStartRequestDTO,
} from 'models/dto/integrations/gitHubInstallDTO';
import {
  IntegrationConnectionDTO,
  IntegrationConnectionGrantCompleteDTO,
  IntegrationConnectionGrantStartDTO,
} from 'models/dto/integrations/integrationConnectionDTO';
import { IntegrationDTO } from 'models/dto/integrations/integrationDTO';
import { IntegrationResultDTO } from 'models/dto/integrations/integrationResultDTO';
import { IntegrationRunDTO } from 'models/dto/integrations/integrationRunDTO';
import { IntegrationRunStatsDTO } from 'models/dto/integrations/integrationRunStatsDTO';
import { IntegrationTypeDTO } from 'models/dto/integrations/integrationTypeDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { Integration } from 'models/integrations/integration';
import { IntegrationConfigBase, IntegrationConfigEnvelope } from 'models/integrations/integrationConfigBase';
import { IntegrationConnection } from 'models/integrations/integrationConnection';
import { getIntegrationConnectionTypes, GitHubConnectModes } from 'models/integrations/integrationConnectionHandler';
import { IntegrationResult } from 'models/integrations/integrationResult';
import { IntegrationRun } from 'models/integrations/integrationRun';
import { IntegrationRunStats } from 'models/integrations/integrationRunStats';
import { getIntegrationTypes, IntegrationTypeHandler } from 'models/integrations/integrationTypeHandler';
import KPI from 'models/kpi/kpi';
import AppError from 'utils/appError';
import { toApiDateRequired } from 'utils/datetime';
import http from './httpService';

export async function apiGetIntegrations(accessToken: string, cache: GlobalDataCache): Promise<Integration[]> {
  try {
    const ar = await http.get<IntegrationDTO[]>(`/integrations`, http.getRequestConfig(accessToken));

    return mapFromIntegrations(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetIntegrationTypes(
  accessToken: string,
  cache: GlobalDataCache,
  t: TFunction<string[]>,
): Promise<IntegrationTypeHandler[]> {
  try {
    // get integration types from API
    const ar = await http.get<IntegrationTypeDTO[]>(`/integrations/types`, http.getRequestConfig(accessToken));
    const integrationTypes = mapFromIntegrationTypes(ar.data, cache);

    // return all types, mapped with data from API where available; the API row names the connection type
    const types = getIntegrationTypes(t);
    const connectionTypes = getIntegrationConnectionTypes(t);
    const result: IntegrationTypeHandler[] = [];
    for (const type of types) {
      const matchedType = integrationTypes.find((it) => it.integrationTypeId === type.integrationTypeId);
      type.integration = matchedType?.integration;
      type.integrationType = matchedType;
      type.connectionType = connectionTypes.find(
        (ct) => ct.integrationConnectionTypeId === matchedType?.integrationConnectionTypeId,
      );
      result.push(type);
    }

    return result;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//A full test run that writes nothing. Authorization alone is tested on the connection (apiTestIntegrationConnection).
export async function apiTestIntegration(
  integrationId: number,
  kpi: KPI,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<IntegrationResult> {
  try {
    const ar = await http.get<IntegrationResultDTO>(
      `/integrations/${integrationId}/test`,
      http.getRequestConfig(accessToken),
    );

    return mapFromIntegrationResult(ar.data, kpi, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Saves the credential and configuration of a shared connection; the API resets it to "never tested".
export async function apiUpdateIntegrationConnection(
  connection: IntegrationConnection,
  accessToken: string,
): Promise<IntegrationConnection> {
  try {
    const ar = await http.put<IntegrationConnectionDTO>(
      `/integrations/connections`,
      mapToIntegrationConnection(connection),
      http.getRequestConfig(accessToken),
    );

    return mapFromIntegrationConnection(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Tests the authorization of a shared connection. The API stores the outcome; an authorization failure arrives as a
//typed controller exception (INTEGRATION0001/0002/0003) after the failure is stored.
export async function apiTestIntegrationConnection(
  integrationConnectionId: number,
  accessToken: string,
): Promise<IntegrationConnection> {
  try {
    const ar = await http.post<IntegrationConnectionDTO>(
      `/integrations/connections/${integrationConnectionId}/test`,
      null,
      http.getRequestConfig(accessToken),
    );

    return mapFromIntegrationConnection(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//The consent page of an OAuthGrant connection, to open in a popup. A start on an environment without the app is
//refused with INTEGRATION0004.
export async function apiStartIntegrationConnectionGrant(
  integrationConnectionId: number,
  accessToken: string,
): Promise<string> {
  try {
    const ar = await http.post<IntegrationConnectionGrantStartDTO>(
      `/integrations/connections/${integrationConnectionId}/grant/start`,
      null,
      http.getRequestConfig(accessToken),
    );

    return ar.data?.url ?? '';
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Turns the code the callback page handed this window into the grant. The API checks that the signed-in person
//started the flow, stores the refresh token encrypted, tests the connection and answers with it.
export async function apiCompleteIntegrationConnectionGrant(
  integrationConnectionId: number,
  code: string,
  state: string,
  accessToken: string,
): Promise<IntegrationConnection> {
  try {
    const body = new IntegrationConnectionGrantCompleteDTO();
    body.code = code;
    body.state = state;
    const ar = await http.post<IntegrationConnectionDTO>(
      `/integrations/connections/${integrationConnectionId}/grant/complete`,
      body,
      http.getRequestConfig(accessToken),
    );

    return mapFromIntegrationConnection(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Starts connecting GitHub: the URL that installs the ISOPlanner GitHub App (or, for an organization that already has it,
//signs in on GitHub and returns to returnUrl), with a state bound to this organization, user and connection. GitHub
//sends the browser back to Settings > Automation (apiCompleteGitHubInstall).
export async function apiStartGitHubInstall(
  integrationConnectionId: number,
  mode: GitHubConnectModes,
  returnUrl: string,
  accessToken: string,
): Promise<string> {
  try {
    const request = new GitHubInstallStartRequestDTO();
    request.mode = mode;
    request.returnUrl = returnUrl;
    const ar = await http.post<GitHubInstallStartDTO>(
      `/integrations/connections/${integrationConnectionId}/github/start`,
      request,
      http.getRequestConfig(accessToken),
    );

    return ar.data.url;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Completes a GitHub install with the query GitHub put on the way back. A refusal arrives as a typed controller
//exception (GITHUB0001-0005); on success the connection holds the installation and is not tested yet.
export async function apiCompleteGitHubInstall(
  code: string,
  installationId: number,
  state: string,
  accessToken: string,
): Promise<IntegrationConnection> {
  try {
    const request = new GitHubInstallCompleteDTO();
    request.code = code;
    request.installationId = installationId;
    request.state = state;

    const ar = await http.post<IntegrationConnectionDTO>(
      `/integrations/github/complete`,
      request,
      http.getRequestConfig(accessToken),
    );

    return mapFromIntegrationConnection(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiValidateIntegrationConfig(
  integrationId: number,
  config: IntegrationConfigBase | undefined,
  accessToken: string,
): Promise<void> {
  try {
    await http.post(
      `/integrations/${integrationId}/validate-config`,
      { config: config ? JSON.stringify(config) : '' },
      http.getRequestConfig(accessToken),
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddIntegrations(
  integrationTypes: IntegrationTypeHandler[],
  accessToken: string,
  cache: GlobalDataCache,
): Promise<IntegrationTypeHandler[]> {
  try {
    if (integrationTypes.some((i) => !i.integration))
      throw new Error('All integration types must have an integration to add');
    const dto = mapToIntegrations(integrationTypes.map((i) => i.integration as Integration));
    const ar = await http.post<IntegrationDTO[]>(`/integrations`, dto, http.getRequestConfig(accessToken));
    const addedIntegrations = mapFromIntegrations(ar.data, cache);

    // update the handlers with the returned data
    const result: IntegrationTypeHandler[] = [];
    for (const type of integrationTypes) {
      const matchedIntegration = addedIntegrations.find((i) => i.integrationTypeId === type.integrationTypeId);
      if (matchedIntegration) {
        type.integration = matchedIntegration;
        result.push(type);
      }
    }

    return result;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateIntegration(
  integrationTypeHandler: IntegrationTypeHandler,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<IntegrationTypeHandler> {
  try {
    if (!integrationTypeHandler.integration) throw new Error('No integration to update');
    const dto = mapToIntegration(integrationTypeHandler.integration);
    const ar = await http.put<IntegrationDTO>(`/integrations`, dto, http.getRequestConfig(accessToken));
    const updatedIntegration = mapFromIntegration(ar.data, cache);
    integrationTypeHandler.integration = updatedIntegration;

    return integrationTypeHandler;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteIntegration(
  integrationTypeHandler: IntegrationTypeHandler,
  accessToken: string,
): Promise<void> {
  try {
    if (!integrationTypeHandler.integration) throw new Error('No integration to delete');
    await http.post<IntegrationDTO>(
      `/integrations/delete/${integrationTypeHandler.integration.integrationId}`,
      null,
      http.getRequestConfig(accessToken),
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetIntegrationConfig(
  id: number,
  accessToken: string,
): Promise<IntegrationConfigEnvelope | undefined> {
  try {
    const ar = await http.get<IntegrationConfigEnvelope>(
      `/integrations/${id}/config`,
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetIntegrationSchedule(
  start: Date,
  end: Date,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Integration[]> {
  try {
    const ar = await http.get<IntegrationDTO[]>(
      `/integrations/schedule?startDate=${toApiDateRequired(start)}&endDate=${toApiDateRequired(end)}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromIntegrations(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetIntegrationRunStats(
  start: Date,
  end: Date,
  accessToken: string,
): Promise<IntegrationRunStats[]> {
  try {
    const ar = await http.get<IntegrationRunStatsDTO[]>(
      `/integrations/stats?startDate=${toApiDateRequired(start)}&endDate=${toApiDateRequired(end)}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromIntegrationRunStats(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetIntegrationScheduleForControl(
  controlId: number,
  start: Date,
  end: Date,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Integration[]> {
  try {
    const ar = await http.get<IntegrationDTO[]>(
      `/integrations/schedule/control/${controlId}?startDate=${toApiDateRequired(start)}&endDate=${toApiDateRequired(
        end,
      )}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromIntegrations(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetIntegrationNextRun(
  id: number,
  kpi: KPI,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<IntegrationRun | undefined> {
  try {
    const ar = await http.get<IntegrationRunDTO>(`/integrations/${id}/nextrun`, http.getRequestConfig(accessToken));
    if (!ar?.data) return undefined;

    return mapFromIntegrationRun(ar.data, kpi, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiReprocessIntegrationRun(
  runId: number,
  kpi: KPI,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<IntegrationRun> {
  try {
    const ar = await http.put<IntegrationRunDTO>(
      `/integrations/runs/${runId}/reprocess`,
      null,
      http.getRequestConfig(accessToken),
    );

    return mapFromIntegrationRun(ar.data, kpi, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetIntegrationForKPI(
  kpiId: number,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Integration | undefined> {
  try {
    const ar = await http.get<IntegrationDTO>(`/integrations/kpi/${kpiId}`, http.getRequestConfig(accessToken));
    if (!ar.data) return undefined;

    return mapFromIntegration(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCancelIntegration(
  integrationGroupId: number,
  accessToken: string,
  reason?: string,
  reasonText?: string,
): Promise<void> {
  try {
    await http.post(
      `/integrations/group/${integrationGroupId}/cancel`,
      { reason, reasonText },
      http.getRequestConfig(accessToken),
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiSubscribeIntegration(integrationGroupId: number, accessToken: string): Promise<void> {
  try {
    await http.post(`/integrations/group/${integrationGroupId}/subscribe`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
